import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "./track";
import Clarity from "@microsoft/clarity";

/**
 * Analytics - drop-in GA4 + Google Ads + GTM + Clarity injector.
 *
 * Ported from form720-ui/src/seo/Analytics.jsx. The two products share no code,
 * so this is a deliberate copy - keep them in step.
 *
 * Reads tracking IDs from env at build time:
 *   REACT_APP_GA4_MEASUREMENT_ID  (e.g. "G-XXXXXXXXXX")
 *   REACT_APP_GOOGLE_ADS_ID       (e.g. "AW-1234567890") - for paid-campaign conversions
 *   REACT_APP_GTM_CONTAINER_ID    (e.g. "GTM-XXXXXXX")
 *   REACT_APP_CLARITY_PROJECT_ID  (e.g. "y5dsfel42g") - session replay / heatmaps
 *
 * If none is set, this component renders nothing - no calls, no scripts, no
 * tracking. That keeps local development clean and prevents shipping with
 * placeholder IDs. Same env-gating contract as TurnstileWidget.
 *
 * SPA note: GA4's automatic page_view only fires on the first load. Because
 * this is a client-side-routed SPA, we set send_page_view:false and fire a
 * page_view on every route change below - so the blog -> pricing -> signup
 * funnel is tracked, not just the landing page.
 *
 * Mount once at the App root (inside <BrowserRouter>).
 */
export default function Analytics() {
  const ga4 = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  const ads = process.env.REACT_APP_GOOGLE_ADS_ID;
  const gtm = process.env.REACT_APP_GTM_CONTAINER_ID;
  const clarity = process.env.REACT_APP_CLARITY_PROJECT_ID;
  const location = useLocation();

  useEffect(() => {
    // react-snap prerenders these routes in a headless browser at build time,
    // running effects and snapshotting the result. Skip all analytics during
    // prerender (react-snap's user agent is "ReactSnap") so third-party tags
    // (Clarity, GA, GTM) are NOT baked into the static HTML. Otherwise each tag
    // loads twice at runtime - once from the prerendered snapshot and once from
    // this effect - double-initializing Clarity, which throws
    // "a[c] is not a function". It also keeps prerender hits out of analytics.
    if (typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap") return;

    if (!ga4 && !ads && !gtm && !clarity) return;
    if (typeof document === "undefined") return;

    if (clarity) {
      Clarity.init(clarity);
    }

    // -- GA4 + Google Ads (gtag.js - one script serves both) --------
    if ((ga4 || ads) && !window.gtag) {
      const primaryId = ga4 || ads;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`;
      document.head.appendChild(s);

      window.dataLayer = window.dataLayer || [];
      // eslint-disable-next-line prefer-rest-params
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      // send_page_view:false - we fire page_view manually per route (SPA).
      if (ga4) window.gtag("config", ga4, { send_page_view: false });
      if (ads) window.gtag("config", ads);
    }

    // -- GTM (Google Tag Manager) -----------------------------------
    if (gtm) {
      // GTM <head> snippet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      const f = document.getElementsByTagName("script")[0];
      const j = document.createElement("script");
      j.async = true;
      j.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtm)}`;
      f.parentNode.insertBefore(j, f);

      // GTM <body> noscript fallback
      const ns = document.createElement("noscript");
      ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtm)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(ns, document.body.firstChild);
    }
  }, [ga4, ads, gtm, clarity]);

  // Fire a GA4 page_view on every route change (SPA navigations don't reload).
  useEffect(() => {
    if (!ga4) return;
    trackPageView(location.pathname + location.search);
  }, [ga4, location.pathname, location.search]);

  return null;
}
